import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import countiesTopology from 'us-atlas/counties-10m.json';
import { DEFAULT_LOCATION_PIN_SCALE, SvgLocationPin } from './MapLocationPin';
import PageLoader from './PageLoader';

const DEFAULT_DIMENSIONS = {
  width: 252,
  height: 192,
  padding: 12,
};

const STATE_FIPS_BY_ABBR = {
  AL: '01',
  AK: '02',
  AZ: '04',
  AR: '05',
  CA: '06',
  CO: '08',
  CT: '09',
  DE: '10',
  DC: '11',
  FL: '12',
  GA: '13',
  HI: '15',
  ID: '16',
  IL: '17',
  IN: '18',
  IA: '19',
  KS: '20',
  KY: '21',
  LA: '22',
  ME: '23',
  MD: '24',
  MA: '25',
  MI: '26',
  MN: '27',
  MS: '28',
  MO: '29',
  MT: '30',
  NE: '31',
  NV: '32',
  NH: '33',
  NJ: '34',
  NM: '35',
  NY: '36',
  NC: '37',
  ND: '38',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VT: '50',
  VA: '51',
  WA: '53',
  WV: '54',
  WI: '55',
  WY: '56',
  AS: '60',
  GU: '66',
  MP: '69',
  PR: '72',
  VI: '78',
};

function normalizeFips(value, width) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const digits = raw.replace(/\D+/g, '');
  if (!digits) return null;
  return digits.padStart(width, '0');
}

function buildFeatureCollection(features) {
  return {
    type: 'FeatureCollection',
    features,
  };
}

function coerceFiniteNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function enrichFeatures(features, width) {
  return features.map((shape) => ({
    ...shape,
    normalizedId: normalizeFips(shape.id, width),
  }));
}

const US_COUNTY_FEATURES = enrichFeatures(
  feature(countiesTopology, countiesTopology.objects.counties).features,
  5,
);
const US_STATE_FEATURES = enrichFeatures(
  feature(countiesTopology, countiesTopology.objects.states).features,
  2,
);
const US_COUNTIES_BY_STATE_FIPS = US_COUNTY_FEATURES.reduce((accumulator, shape) => {
  const countyFips = shape.normalizedId;
  if (!countyFips) return accumulator;
  const stateFips = countyFips.slice(0, 2);
  if (!accumulator[stateFips]) accumulator[stateFips] = [];
  accumulator[stateFips].push(shape);
  return accumulator;
}, {});
const US_STATE_FEATURE_BY_FIPS = US_STATE_FEATURES.reduce((accumulator, shape) => {
  if (shape.normalizedId) accumulator[shape.normalizedId] = shape;
  return accumulator;
}, {});

function resolveStateAbbr(market, countyMapPayload) {
  const regionState = String(market?.region_name || '').match(/,\s*([A-Za-z]{2})\s*$/)?.[1];
  const rawState = [
    regionState,
    market?.state_abbr,
    market?.state_name,
    countyMapPayload?.state?.state_abbr,
  ].find((value) => typeof value === 'string' && value.trim());
  const normalized = String(rawState || '').trim().toUpperCase();
  return normalized.length === 2 ? normalized : null;
}

function resolveStateFips(market, countyMapPayload) {
  const explicitFips = normalizeFips(countyMapPayload?.state?.state_fips, 2);
  if (explicitFips) return explicitFips;
  const stateAbbr = resolveStateAbbr(market, countyMapPayload);
  return stateAbbr ? STATE_FIPS_BY_ABBR[stateAbbr] || null : null;
}

function resolveStateName(market, countyMapPayload) {
  const stateFips = resolveStateFips(market, countyMapPayload);
  const topologyStateName = stateFips
    ? US_STATE_FEATURE_BY_FIPS[stateFips]?.properties?.name
    : null;
  return (
    market?.state_name_full
    || market?.state_name
    || countyMapPayload?.state?.name
    || topologyStateName
    || resolveStateAbbr(market, countyMapPayload)
    || 'State'
  );
}

function resolveMarketLabel(market) {
  return market?.region_name || market?.market_name || market?.name || market?.slug || 'selected market';
}

function countyPinFromMarketCounties({ path, countyFeatures, highlightedCountyFips, marketLabel }) {
  if (!highlightedCountyFips.size) return null;
  const countyPoints = countyFeatures
    .filter((shape) => highlightedCountyFips.has(shape.normalizedId))
    .map((shape) => path.centroid(shape))
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));

  if (!countyPoints.length) return null;

  return {
    x: countyPoints.reduce((sum, [x]) => sum + x, 0) / countyPoints.length,
    y: countyPoints.reduce((sum, [, y]) => sum + y, 0) / countyPoints.length,
    ariaLabel: `State locator for ${marketLabel}`,
  };
}

function projectedMarketPin(projection, market) {
  if (typeof projection !== 'function') return null;
  const latitude = coerceFiniteNumber(market?.latitude);
  const longitude = coerceFiniteNumber(market?.longitude);
  if (latitude == null || longitude == null) return null;
  const point = projection([longitude, latitude]);
  if (!Array.isArray(point) || point.length < 2) return null;
  const [x, y] = point;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return {
    x,
    y,
    ariaLabel: `State locator for ${resolveMarketLabel(market)}`,
  };
}

export function buildStateCountyLocatorMapModel(
  market,
  countyMapPayload = null,
  dimensions = DEFAULT_DIMENSIONS,
) {
  const stateFips = resolveStateFips(market, countyMapPayload);
  const countyFeatures = stateFips ? US_COUNTIES_BY_STATE_FIPS[stateFips] || [] : [];
  const stateFeature = stateFips ? US_STATE_FEATURE_BY_FIPS[stateFips] || null : null;
  if (!countyFeatures.length || !stateFeature) {
    return null;
  }

  const projection = geoMercator().fitExtent(
    [
      [dimensions.padding, dimensions.padding],
      [dimensions.width - dimensions.padding, dimensions.height - dimensions.padding],
    ],
    buildFeatureCollection(countyFeatures),
  );
  const path = geoPath(projection);
  const countyPaths = countyFeatures
    .map((shape) => ({
      geoId: shape.normalizedId,
      d: path(shape),
    }))
    .filter((shape) => Boolean(shape.d));
  const marketCountyFips = new Set(
    (countyMapPayload?.market_counties || [])
      .map((county) => normalizeFips(county?.county_fips, 5))
      .filter(Boolean),
  );
  const pin = countyPinFromMarketCounties({
    path,
    countyFeatures,
    highlightedCountyFips: marketCountyFips,
    marketLabel: resolveMarketLabel(market),
  }) || projectedMarketPin(projection, market);

  return {
    width: dimensions.width,
    height: dimensions.height,
    stateName: resolveStateName(market, countyMapPayload),
    stateAbbr: resolveStateAbbr(market, countyMapPayload),
    outlinePath: path(stateFeature),
    countyPaths,
    pin,
  };
}

export default function StateCountyLocatorMap({
  market,
  countyMapPayload = null,
  loading = false,
}) {
  const model = buildStateCountyLocatorMapModel(market, countyMapPayload);
  const stateName = resolveStateName(market, countyMapPayload);
  const marketLabel = resolveMarketLabel(market);

  return (
    <div className="research-market-visual__locator-shell">
      <div className="research-market-visual__locator-card" data-testid="market-state-locator">
        <div className="research-market-visual__locator-head">
          <h3 className="research-market-visual__locator-title">{stateName}</h3>
        </div>

        <div className="research-market-visual__locator-body">
          {model ? (
            <svg
              className="research-market-visual__locator-map"
              viewBox={`0 0 ${model.width} ${model.height}`}
              role="img"
              aria-label={`State locator for ${marketLabel} in ${stateName}`}
              data-testid="market-state-locator-svg"
            >
              {model.countyPaths.map((shape) => (
                <path
                  key={shape.geoId}
                  className="research-market-visual__locator-shape"
                  d={shape.d}
                  data-testid="market-state-locator-county"
                  aria-hidden="true"
                />
              ))}
              {model.outlinePath ? (
                <path
                  className="research-market-visual__locator-outline"
                  d={model.outlinePath}
                  aria-hidden="true"
                />
              ) : null}
              {model.pin ? (
                <g data-testid="market-state-locator-pin">
                  <SvgLocationPin
                    pin={model.pin}
                    scale={DEFAULT_LOCATION_PIN_SCALE * 0.78}
                    className="research-market-visual__locator-pin"
                  />
                </g>
              ) : null}
            </svg>
          ) : (
            <div
              className={[
                'research-market-visual__locator-placeholder',
                loading ? 'research-market-visual__locator-placeholder--loading' : '',
              ].filter(Boolean).join(' ')}
              role="status"
            >
              {loading ? (
                <PageLoader compact label="Loading state locator" />
              ) : (
                <>
                  <strong>State locator unavailable</strong>
                  <p>The state outline or market coordinates are not ready for this market yet.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
